// Hook for checking app updates and managing service worker
// With automatic hourly update checks
import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../utils/api';

const APP_VERSION = '2.0.0';
const UPDATE_CHECK_INTERVAL = 60 * 60 * 1000; // 1 hour in milliseconds
const INITIAL_CHECK_DELAY = 10 * 1000; // 10 seconds after app load

export const useAppUpdate = (options = {}) => {
  const {
    autoCheck = true, // Enable automatic update checks
    checkInterval = UPDATE_CHECK_INTERVAL,
    initialDelay = INITIAL_CHECK_DELAY,
    onUpdateAvailable = null, // Callback when update is found
  } = options;

  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [updateInfo, setUpdateInfo] = useState(null);
  const [checking, setChecking] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);
  const [swRegistration, setSwRegistration] = useState(null);
  const intervalRef = useRef(null);
  const initialCheckDone = useRef(false);

  // Check for updates from API
  const checkForUpdate = useCallback(async (silent = false) => {
    // Don't check if already checking
    if (checking) return false;
    
    try {
      if (!silent) setChecking(true);
      
      const response = await api.post(`/push/check-update?client_version=${APP_VERSION}`);
      const data = response.data;
      
      setLastChecked(new Date());
      
      if (data.update_available) {
        const info = {
          version: data.current_version,
          release_notes: data.release_notes,
          force_update: data.force_update,
          source: 'api',
          checkedAt: new Date().toISOString()
        };
        
        setUpdateAvailable(true);
        setUpdateInfo(info);
        
        // Call callback if provided
        if (onUpdateAvailable) {
          onUpdateAvailable(info);
        }
        
        // Store in localStorage for persistence
        localStorage.setItem('pending_update', JSON.stringify(info));
        
        console.log('🔄 Update available:', data.current_version);
        return true;
      }
      
      // Clear any stored pending update
      localStorage.removeItem('pending_update');
      return false;
      
    } catch (error) {
      console.error('Failed to check for updates:', error);
      return false;
    } finally {
      if (!silent) setChecking(false);
    }
  }, [checking, onUpdateAvailable]);

  // Check for service worker updates
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      // Listen for service worker messages
      const handleMessage = (event) => {
        if (event.data && event.data.type === 'SW_UPDATED') {
          console.log('🔄 Service Worker updated to version:', event.data.version);
          setUpdateAvailable(true);
          setUpdateInfo(prev => ({
            ...prev,
            version: event.data.version,
            source: 'service-worker',
            checkedAt: new Date().toISOString()
          }));
        }
      };
      
      navigator.serviceWorker.addEventListener('message', handleMessage);

      // Get registration
      navigator.serviceWorker.ready.then((registration) => {
        setSwRegistration(registration);
        registration.update();
      });

      return () => {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
      };
    }
  }, []);

  // Auto-check for updates
  useEffect(() => {
    if (!autoCheck) return;
    
    // Initial check after delay
    const initialTimeout = setTimeout(() => {
      if (!initialCheckDone.current) {
        initialCheckDone.current = true;
        checkForUpdate(true); // Silent check
      }
    }, initialDelay);

    // Set up periodic checks
    intervalRef.current = setInterval(() => {
      checkForUpdate(true); // Silent check
      
      // Also update service worker
      if (swRegistration) {
        swRegistration.update();
      }
    }, checkInterval);

    return () => {
      clearTimeout(initialTimeout);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoCheck, checkInterval, initialDelay, checkForUpdate, swRegistration]);

  // Check for stored pending update on mount
  useEffect(() => {
    const stored = localStorage.getItem('pending_update');
    if (stored) {
      try {
        const info = JSON.parse(stored);
        // Only restore if not dismissed
        const dismissed = sessionStorage.getItem('update_dismissed');
        if (dismissed !== info.version) {
          setUpdateInfo(info);
          setUpdateAvailable(true);
        }
      } catch (e) {
        localStorage.removeItem('pending_update');
      }
    }
  }, []);

  // Apply update
  const applyUpdate = useCallback(() => {
    // Clear stored update info
    localStorage.removeItem('pending_update');
    sessionStorage.removeItem('update_dismissed');
    
    if (swRegistration && swRegistration.waiting) {
      // Tell the waiting service worker to skip waiting
      swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
    
    // Reload the page
    window.location.reload();
  }, [swRegistration]);

  // Dismiss update notification
  const dismissUpdate = useCallback(() => {
    setUpdateAvailable(false);
    // Store dismissal in sessionStorage (not localStorage to show again next session)
    if (updateInfo?.version) {
      sessionStorage.setItem('update_dismissed', updateInfo.version);
    }
  }, [updateInfo]);

  // Force show update (for force updates)
  useEffect(() => {
    if (updateInfo?.force_update) {
      // Always show force updates
      setUpdateAvailable(true);
      sessionStorage.removeItem('update_dismissed');
    }
  }, [updateInfo?.force_update]);

  return {
    // State
    updateAvailable,
    updateInfo,
    checking,
    lastChecked,
    currentVersion: APP_VERSION,
    
    // Actions
    checkForUpdate,
    applyUpdate,
    dismissUpdate,
    
    // Utilities
    isForceUpdate: updateInfo?.force_update || false,
    timeUntilNextCheck: intervalRef.current ? checkInterval : null
  };
};

export default useAppUpdate;
